import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { BusinessStatisticItem } from './parts/BusinessStatisticItem/BusinessStatisticItem';
import { BusinessStatisticMainItem } from './parts/BusinessStatisticMainItem/BusinessStatisticMainItem';
import { BusinessStatisticSection } from './parts/BusinessStatisticSection/BusinessStatisticSection';
import { BusinessStatisticSummaryProps } from './types';

export const BusinessStatisticSummary = ({
    statistic,
    hideDetails,
    isService,
    ...other
}: BusinessStatisticSummaryProps) => {
    const t = useTranslation();
    const {
        coverage,
        involved,
        showsInGrid,
        shows,
        subscribers,
        interactions,
        clicks,
        visits,
        gmv,
        carts,
        purchases,
    } = statistic;

    const enableNewStat = useExperimentFlag('enable_gmv_visits_purchases_carts') && !isService;

    return (
        <Flex width="100%" flexWrap="wrap" direction="row" alignItems="stretch" {...other}>
            {coverage ? (
                <BusinessStatisticSection
                    title={t('Охват')}
                    helpMessage={
                        <Typography variant="body">
                            {t(
                                'Количество уникальных пользователей, которые увидели как органические, так и продвигаемые посты.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Сколько уникальных пользователей хотя бы один раз увидели ваши посты',
                                )}
                                displayValue={coverage.total.display}
                                diffValue={coverage.total.diffValue}
                                diffDisplay={coverage.total.diffDisplay}
                            />
                            {hideDetails ? null : (
                                <>
                                    {coverage.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={coverage.organic.display}
                                            diffValue={coverage.organic.diffValue}
                                            diffDisplay={coverage.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {coverage.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={coverage.boosted.display}
                                            diffValue={coverage.boosted.diffValue}
                                            diffDisplay={coverage.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    }
                    borderTop="none"
                    borderRight={{ xs: 'none', sm: '1px solid var(--divider)' }}
                />
            ) : null}

            {involved ? (
                <BusinessStatisticSection
                    title={t('Вовлеченные аккаунты')}
                    helpMessage={
                        <Typography variant="body">
                            {t(
                                'Количество уникальных пользователей, которые взаимодействовали с вашими постами. Метрика считается отдельно для органических и продвигаемых публикаций.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Сколько уникальных пользователей взаимодействовали с вашими постами',
                                )}
                                displayValue={involved.total.display}
                                diffValue={involved.total.diffValue}
                                diffDisplay={involved.total.diffDisplay}
                            />
                            {hideDetails ? null : (
                                <>
                                    {involved.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={involved.organic.display}
                                            diffValue={involved.organic.diffValue}
                                            diffDisplay={involved.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {involved.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={involved.boosted.display}
                                            diffValue={involved.boosted.diffValue}
                                            diffDisplay={involved.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    }
                    borderTop={{ xs: '1px solid var(--divider)', sm: 'none' }}
                />
            ) : null}
            {showsInGrid ? (
                <BusinessStatisticSection
                    title={t('Показы в ленте')}
                    helpMessage={
                        <Typography variant="body" display="inline">
                            {t(
                                'Сколько раз пользователи увидели ваши посты. Метрика считается отдельно для органических и продвигаемых публикаций.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Сколько раз пользователи увидели ваши посты в ленте Ритма',
                                )}
                                displayValue={showsInGrid.total.display}
                                diffValue={showsInGrid.total.diffValue}
                                diffDisplay={showsInGrid.total.diffDisplay}
                            />
                            {hideDetails ? null : (
                                <>
                                    {showsInGrid.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={showsInGrid.organic.display}
                                            diffValue={showsInGrid.organic.diffValue}
                                            diffDisplay={showsInGrid.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {showsInGrid.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={showsInGrid.boosted.display}
                                            diffValue={showsInGrid.boosted.diffValue}
                                            diffDisplay={showsInGrid.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    }
                    borderRight={{ xs: 'none', sm: '1px solid var(--divider)' }}
                />
            ) : null}
            {shows ? (
                <BusinessStatisticSection
                    title={t('Показы с действием')}
                    helpMessage={
                        <Typography variant="body" display="inline">
                            {t(
                                'Количество показов, после которых пользователи взаимодействовали с постом: открыли пост на ya.ru, в веб-версии или приложении, навели курсор на пост, поставили реакцию или сохранили публикацию. Метрика считается отдельно для органических и продвигаемых публикаций.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Количество показов, после которых пользователи взаимодействовали с постом',
                                )}
                                displayValue={shows.total.display}
                                diffValue={shows.total.diffValue}
                                diffDisplay={shows.total.diffDisplay}
                            />
                            {hideDetails ? null : (
                                <>
                                    {shows.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={shows.organic.display}
                                            diffValue={shows.organic.diffValue}
                                            diffDisplay={shows.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {shows.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={shows.boosted.display}
                                            diffValue={shows.boosted.diffValue}
                                            diffDisplay={shows.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    }
                />
            ) : null}
            {enableNewStat && visits ? (
                <BusinessStatisticSection
                    title={t('Визиты')}
                    helpMessage={
                        <Typography variant="body" display="inline">
                            {t(
                                'Количество переходов на сайт с поста или профиля в «Ритме». Визиты рассчитываются на основе данных счётчика Яндекс Метрики, установленного на вашем сайте, — отдельно для органических и продвигаемых публикаций.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Количество визитов на сайт после перехода из постов или профиля',
                                )}
                                displayValue={visits.total.display}
                                diffValue={visits.total.diffValue}
                                diffDisplay={visits.total.diffDisplay}
                            />
                            {hideDetails ? null : (
                                <>
                                    {visits.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={visits.organic.display}
                                            diffValue={visits.organic.diffValue}
                                            diffDisplay={visits.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {visits.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={visits.boosted.display}
                                            diffValue={visits.boosted.diffValue}
                                            diffDisplay={visits.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    }
                />
            ) : null}
            {enableNewStat && purchases ? (
                <BusinessStatisticSection
                    title={t('Покупки')}
                    helpMessage={
                        <Typography variant="body" display="inline">
                            {t(
                                'Количество покупок товаров и услуг, совершённых на сайте после перехода с поста или профиля в «Ритме». Показатель считается на основании данных Яндекс Метрики для органических и продвигаемых публикаций.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Сколько покупок пользователи совершили после перехода из постов или профиля',
                                )}
                                displayValue={purchases.total.display}
                                diffValue={purchases.total.diffValue}
                                diffDisplay={purchases.total.diffDisplay}
                            />
                            {hideDetails ? null : (
                                <>
                                    {purchases.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={purchases.organic.display}
                                            diffValue={purchases.organic.diffValue}
                                            diffDisplay={purchases.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {purchases.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={purchases.boosted.display}
                                            diffValue={purchases.boosted.diffValue}
                                            diffDisplay={purchases.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    }
                />
            ) : null}
            {enableNewStat && carts ? (
                <BusinessStatisticSection
                    title={t('Корзины')}
                    helpMessage={
                        <Typography variant="body" display="inline">
                            {t(
                                'Количество добавлений товаров в корзину после перехода с поста или профиля в «Ритме». Показатель считается на основании данных Яндекс Метрики отдельно для оплаченных и неоплаченных корзин.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Сколько раз пользователи добавили товары в корзину после перехода из постов или профиля',
                                )}
                                displayValue={carts.total.display}
                                diffValue={carts.total.diffValue}
                                diffDisplay={carts.total.diffDisplay}
                            />
                            {hideDetails ? null : (
                                <>
                                    {carts.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={carts.organic.display}
                                            diffValue={carts.organic.diffValue}
                                            diffDisplay={carts.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {carts.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={carts.boosted.display}
                                            diffValue={carts.boosted.diffValue}
                                            diffDisplay={carts.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    }
                />
            ) : null}
            {enableNewStat && gmv ? (
                <BusinessStatisticSection
                    title={t('GMV')}
                    helpMessage={
                        <Typography variant="body" display="inline">
                            {t(
                                'GMV (Gross Merchandise Value) — общий объём продаж товаров и услуг, совершённых по ссылкам с поста или профиля в «Ритме» за выбранный период. Показатель считается на основании данных Яндекс Метрики отдельно для органических и продвигаемых публикаций.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Общий объём продаж товаров и услуг, совершённых пользователями после перехода по ссылкам из постов или профиля',
                                )}
                                displayValue={gmv.total.display}
                                diffValue={gmv.total.diffValue}
                                diffDisplay={gmv.total.diffDisplay}
                            />
                            {hideDetails ? null : (
                                <>
                                    {gmv.organic ? (
                                        <BusinessStatisticItem
                                            title={t('Органика')}
                                            displayValue={gmv.organic.display}
                                            diffValue={gmv.organic.diffValue}
                                            diffDisplay={gmv.organic.diffDisplay}
                                        />
                                    ) : null}
                                    {gmv.boosted ? (
                                        <BusinessStatisticItem
                                            title={t('Платное продвижение')}
                                            displayValue={gmv.boosted.display}
                                            diffValue={gmv.boosted.diffValue}
                                            diffDisplay={gmv.boosted.diffDisplay}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    }
                />
            ) : null}
            {interactions ? (
                <BusinessStatisticSection
                    title={t('Взаимодействия')}
                    helpMessage={
                        <Typography variant="body" display="inline">
                            {t(
                                'Cколько раз пользователи взаимодействовали с вашими постами: переходили по ссылкам, ставили реакции, делились и сохраняли публикации. Метрика считается отдельно для органических и продвигаемых публикаций.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <>
                            <BusinessStatisticMainItem
                                text={t(
                                    'Сколько раз пользователи взаимодействовали с вашими постами: ставили реакции, делились публикациями, сохраняли их, переходили по ссылкам',
                                )}
                                displayValue={interactions.total.display}
                                diffValue={interactions.total.diffValue}
                                diffDisplay={interactions.total.diffDisplay}
                            />
                            <Flex width="100%">
                                <BusinessStatisticItem
                                    title={t('Клики по ссылке')}
                                    displayValue={interactions.clicks.display}
                                    diffValue={interactions.clicks.diffValue}
                                    diffDisplay={interactions.clicks.diffDisplay}
                                />
                                {hideDetails || !(clicks?.organic || clicks?.boosted) ? null : (
                                    <Flex
                                        direction="row"
                                        width="100%"
                                        spacing={1}
                                        mt={1}
                                        pl={0.5}
                                        overflow="hidden"
                                    >
                                        <Flex flexGrow={1}>
                                            {clicks.organic ? (
                                                <BusinessStatisticItem
                                                    pt={1}
                                                    title={t('Органика')}
                                                    displayValue={clicks.organic.display}
                                                    diffValue={clicks.organic.diffValue}
                                                    diffDisplay={clicks.organic.diffDisplay}
                                                    withNestedListMarker
                                                />
                                            ) : null}
                                            {clicks.boosted ? (
                                                <BusinessStatisticItem
                                                    pt={1.75}
                                                    title={t('Платное продвижение')}
                                                    displayValue={clicks.boosted.display}
                                                    diffValue={clicks.boosted.diffValue}
                                                    diffDisplay={clicks.boosted.diffDisplay}
                                                    withNestedListMarker
                                                />
                                            ) : null}
                                        </Flex>
                                    </Flex>
                                )}
                                <BusinessStatisticItem
                                    title={t('Лайки')}
                                    displayValue={interactions.likes.display}
                                    diffValue={interactions.likes.diffValue}
                                    diffDisplay={interactions.likes.diffDisplay}
                                />
                                <BusinessStatisticItem
                                    title={t('Шаринги')}
                                    displayValue={interactions.shares.display}
                                    diffValue={interactions.shares.diffValue}
                                    diffDisplay={interactions.shares.diffDisplay}
                                />
                                <BusinessStatisticItem
                                    title={t('Сохранения')}
                                    displayValue={interactions.saves.display}
                                    diffValue={interactions.saves.diffValue}
                                    diffDisplay={interactions.saves.diffDisplay}
                                />
                            </Flex>
                        </>
                    }
                />
            ) : null}
            {subscribers ? (
                <BusinessStatisticSection
                    title={t('Подписчики')}
                    helpMessage={
                        <Typography variant="body" display="inline">
                            {t(
                                'Количество аккаунтов, подписавшихся на профиль за весь срок существования профиля с изменением в количестве подписавшихся за выбранный период.',
                            )}
                        </Typography>
                    }
                    statsSlot={
                        <BusinessStatisticMainItem
                            text={t('Количество аккаунтов, подписавшихся на профиль')}
                            displayValue={subscribers.total.display}
                            diffValue={subscribers.total.diffValue}
                            diffDisplay={subscribers.total.diffDisplay}
                        />
                    }
                    borderRight="none"
                />
            ) : null}
        </Flex>
    );
};
